/**
 * Bet Detail Modal - shows full bet info with odds graph
 */

import { useRef, useEffect } from "preact/hooks";
import type { Bet } from "../api/types";

interface BetDetailModalProps {
  bet: Bet;
  currentUserId: string;
  isAdmin: boolean;
  onClose: () => void;
  onPlaceWager: () => void;
  onResolve?: (outcome: "YES" | "NO") => void;
  onDelete?: () => void;
}

export function BetDetailModal({
  bet,
  currentUserId,
  isAdmin,
  onClose,
  onPlaceWager,
  onResolve,
  onDelete,
}: BetDetailModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const probability = calculateProbability(bet.yesPool, bet.noPool);

  // Draw odds graph on mount and when pools change
  useEffect(() => {
    drawOddsGraph(canvasRef.current, bet.yesPool, bet.noPool);
  }, [bet.yesPool, bet.noPool]);

  const totalPool = bet.yesPool + bet.noPool;

  return (
    <div class="modal-overlay" onClick={onClose}>
      <div class="modal bet-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button class="modal-close" onClick={onClose}>
          &times;
        </button>

        <h3>{bet.description}</h3>

        {bet.resolutionCriteria && (
          <p class="hint">Resolution: {bet.resolutionCriteria}</p>
        )}

        <div class="bet-detail-graph">
          <canvas ref={canvasRef} />
        </div>

        <div class="bet-detail-pools">
          <div class="pool-detail">
            <div class="pool-detail-header">YES</div>
            <div class="pool-detail-amount">${bet.yesPool}</div>
            <div class="pool-detail-percent">
              {Math.round(probability * 100)}%
            </div>
          </div>
          <div class="pool-detail">
            <div class="pool-detail-header">NO</div>
            <div class="pool-detail-amount">${bet.noPool}</div>
            <div class="pool-detail-percent">
              {Math.round((1 - probability) * 100)}%
            </div>
          </div>
        </div>

        <div class="bet-detail-total">
          <span class="hint">Total pool: ${totalPool}</span>
        </div>

        <div class="bet-detail-actions">
          <button class="btn btn-primary" onClick={onPlaceWager}>
            Place Wager
          </button>
        </div>

        {isAdmin && bet.status === "active" && onResolve && (
          <div class="admin-resolve">
            <button class="btn btn-secondary" onClick={() => onResolve("YES")}>
              Resolve YES
            </button>
            <button class="btn btn-secondary" onClick={() => onResolve("NO")}>
              Resolve NO
            </button>
          </div>
        )}

        {(isAdmin || bet.createdBy === currentUserId) &&
          bet.status === "active" &&
          onDelete && (
            <div class="bet-delete-action">
              <button class="btn btn-danger" onClick={onDelete}>
                Delete Bet
              </button>
            </div>
          )}
      </div>
    </div>
  );
}

function calculateProbability(yesPool: number, noPool: number): number {
  const total = yesPool + noPool;
  if (total === 0) return 0.5;
  return yesPool / total;
}

function drawOddsGraph(
  canvas: HTMLCanvasElement | null,
  yesPool: number,
  noPool: number,
): void {
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Set canvas size to match its display size for crisp rendering
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  // Clear canvas
  ctx.clearRect(0, 0, rect.width, rect.height);

  // Get theme-aware colors
  const isDark = document.documentElement.getAttribute("data-theme") === "dark";
  const yesColor = "#16a34a"; // Green for YES
  const noColor = isDark ? "#9ca3af" : "#6b7280"; // Gray for NO
  const borderColor = isDark ? "#3a3a3a" : "#d0d0d0";

  const total = yesPool + noPool;
  const yesPercent = total === 0 ? 0.5 : yesPool / total;
  const noPercent = 1 - yesPercent;

  // Fixed layout: YES always on left half, NO always on right half
  const width = rect.width;
  const height = rect.height;
  const labelHeight = 24;
  const barHeight = height - labelHeight - 8; // Leave room for labels at bottom
  const barY = 0;
  const labelY = height - labelHeight + 4;
  const halfWidth = width / 2;
  const gap = 6; // Gap between bars

  // Draw YES bar (left half) - fill based on percentage
  const yesBarWidth = (halfWidth - gap) * yesPercent;
  if (yesBarWidth > 0) {
    ctx.fillStyle = yesColor;
    ctx.fillRect(halfWidth - gap - yesBarWidth, barY, yesBarWidth, barHeight);
  }
  // Draw empty YES background
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(0, barY, halfWidth - gap, barHeight);

  // Draw NO bar (right half) - fill based on percentage
  const noBarWidth = (halfWidth - gap) * noPercent;
  if (noBarWidth > 0) {
    ctx.fillStyle = noColor;
    ctx.fillRect(halfWidth + gap, barY, noBarWidth, barHeight);
  }
  // Draw empty NO background
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(halfWidth + gap, barY, halfWidth - gap, barHeight);

  // Draw labels below bars
  ctx.font = "600 14px Inter, sans-serif";
  ctx.textBaseline = "top";

  // YES label (left)
  ctx.fillStyle = yesColor;
  ctx.textAlign = "center";
  ctx.fillText(
    `YES ${Math.round(yesPercent * 100)}%`,
    (halfWidth - gap) / 2,
    labelY,
  );

  // NO label (right)
  ctx.fillStyle = noColor;
  ctx.textAlign = "center";
  ctx.fillText(
    `NO ${Math.round(noPercent * 100)}%`,
    halfWidth + gap + (halfWidth - gap) / 2,
    labelY,
  );
}
